import { HomeFilled, PlusOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { Col, Row, Space } from 'antd';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import { StyledPageWrapper } from 'components/new-ui/base/wrappers/styles';
import useBreadcrumb from 'hooks/useBreadcrumb';
import ArchiveFilter from 'components/new-ui/composed/archive-filter/ArchiveFilter';
import Button from 'components/new-ui/base/button/Button';
import useManifests from 'hooks/useManifests';
import { useNavigate } from 'react-router-dom';
import ManifestsTable from 'views/manifests/components/manifests-table/ManifestsTable';
import ManifestsFiltersModal from 'views/manifests/dashboard/components/ManifestsFiltersModal';
import UnarchiveManifestModal from 'views/manifests/components/UnarchiveManifestModal';
import { Manifest } from 'store/manifests/types';
import Notification from 'components/ui/base/notification/Notification';
import ArchiveManifestModal from 'views/manifests/components/ArchiveManifestModal';
import AssignManifestModal from 'views/manifests/components/assign-manifest/AssignManifestModal';
import useManifestsTable from 'views/manifests/hooks/useManifestsTable';
import ManifestFilterTags from 'views/manifests/components/ManifestFilterTags';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import DatePicker from 'components/new-ui/base/date-picker/DatePicker';
import FreightAdminStatsCards from './components/FreightAdminStatsCards';
import { useRequestPromise } from 'hooks/useRequest';
import { getStatistics } from 'utils/requests/getStatistics';
import { getErrorMessage } from 'utils/getErrorMessage';
import useIndividuals from 'hooks/useIndividuals';
import useTeams from 'hooks/useTeams';

type Modal = 'filter' | 'assignManifest' | 'archiveManifest' | 'unarchiveManifest';

const FreightForwarderAdminDashboard = () => {
    const { archiveManifest, unarchiveManifest } = useManifests();
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const { listIndividuals } = useIndividuals();
    const { listTeams } = useTeams();
    const navigate = useNavigate();

    const {
        tags,
        queryInputVal,
        appliedParams,
        archived,
        isLoadingManifests,
        data,
        setArchived,
        setQueryInputVal,
        setPage,
        clearAll,
        clearFilter,
        date,
        filters,
        pagination,
        refetchManifests,
        handleTableChange,
        handleFiltersChange,
        handleDateChange,
    } = useManifestsTable();
    const {
        data: statsData,
        error: statsError,
        isLoading: isLoadingStats,
        refetch: refetchStatsData,
    } = useRequestPromise(() => getStatistics());

    const [selectedManifest, setSelectedManifest] = useState<Manifest | null>(null);
    const [archiving, setArchiving] = useState(false);
    const [unarchiving, setUnarchiving] = useState(false);
    const [openModal, setOpenModal] = useState<Modal | null>(null);

    const { sort, sortDirection } = appliedParams;

    const handleManifestClick = (record: Manifest) => {
        const manifestId = record.id;
        navigate(`/manifests/${manifestId}`);
    };

    const handleOpenArchiveModal = (m: Manifest) => {
        setSelectedManifest(m);
        setOpenModal('archiveManifest');
    };

    const handleOpenUnarchiveModal = (m: Manifest) => {
        setSelectedManifest(m);
        setOpenModal('unarchiveManifest');
    };

    const handleConfirmArchive = async () => {
        if (!selectedManifest) return;
        try {
            setArchiving(true);
            await archiveManifest(selectedManifest.id);
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Manifest Archived',
                description: 'The manifest has been successfully added to the archive',
            });
            refetchManifests();
            refetchStatsData();
            setOpenModal(null);
            setSelectedManifest(null);
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Manifest Archiving Error',
                description: errorMessage || 'Could not archive manifest. Please try again.',
            });
        } finally {
            setArchiving(false);
        }
    };

    const handleConfirmUnarchive = async () => {
        if (!selectedManifest) return;
        try {
            setUnarchiving(true);
            await unarchiveManifest(selectedManifest.id);
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Manifest Unarchived',
                description: 'The manifest will now be activated.',
            });
            refetchManifests();
            refetchStatsData();
            setOpenModal(null);
            setSelectedManifest(null);
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Manifest Activation Error',
                description: errorMessage || 'Could not activate the manifest. Please try again.',
            });
        } finally {
            setUnarchiving(false);
        }
    };

    useEffect(() => {
        setBreadcrumbRoutes([
            {
                breadcrumbName: 'Dashboard',
                path: '/home',
            },
        ]);
    }, [setBreadcrumbRoutes]);

    useEffect(() => {
        listTeams({ size: 200 });
        listIndividuals({ size: 200 });
    }, [listTeams, listIndividuals]);

    return (
        <StyledPageWrapper>
            <Space style={{ width: '100%' }} direction="vertical" size={24}>
                <PageHeader
                    title="Dashboard"
                    icon={<HomeFilled />}
                    sidebar={
                        <Space size={16}>
                            <ArchiveFilter
                                value={archived ? 'archived' : 'active'}
                                onChange={(val) => {
                                    if (val === 'active') {
                                        setArchived(undefined);
                                    } else {
                                        setArchived(true);
                                    }
                                }}
                            />
                            <Button
                                icon={<PlusOutlined />}
                                type="primary"
                                onClick={() => {
                                    navigate('/manifests/new');
                                }}
                            >
                                New Manifest
                            </Button>
                        </Space>
                    }
                />

                <FreightAdminStatsCards
                    data={statsData}
                    error={statsError}
                    loading={isLoadingStats}
                    refetch={refetchStatsData}
                />

                <Row justify="space-between">
                    <Col>
                        <Space direction="vertical">
                            <Space size={8}>
                                <SearchBar
                                    inputPlaceholder="Search manifests"
                                    value={queryInputVal}
                                    onSearch={(val) => {
                                        setQueryInputVal(val);
                                        setPage(0);
                                    }}
                                    onClear={() => {
                                        setQueryInputVal('');
                                        setPage(0);
                                    }}
                                />
                                <Button onClick={() => setOpenModal('filter')}>Filter</Button>
                            </Space>
                        </Space>
                    </Col>
                    <Col>
                        <DatePicker value={date} onChange={handleDateChange} />
                    </Col>
                </Row>

                <Row>
                    <ManifestFilterTags
                        tags={tags}
                        ownerId={appliedParams.ownerId}
                        teamId={appliedParams.teamId}
                        createdBy={appliedParams.createdBy}
                        onClearTag={clearFilter}
                        onClearAll={clearAll}
                    />
                </Row>

                <ManifestsTable
                    manifests={data?.list}
                    loading={isLoadingManifests}
                    onArchiveManifest={handleOpenArchiveModal}
                    onUnarchiveManifest={handleOpenUnarchiveModal}
                    onAssignManifest={(manifest) => {
                        setSelectedManifest(manifest);
                        setOpenModal('assignManifest');
                    }}
                    onRowClick={handleManifestClick}
                    onChange={handleTableChange}
                    paginator={pagination}
                    currentSortDirection={sortDirection}
                    currentSortParam={sort}
                />
            </Space>

            <ManifestsFiltersModal
                visible={openModal === 'filter'}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onClose={() => setOpenModal(null)}
                onClearAll={() => clearAll()}
                onClearFilter={clearFilter}
                optionalFilters={{
                    team: true,
                    assignedTo: true,
                }}
            />

            {selectedManifest && (
                <AssignManifestModal
                    manifest={selectedManifest}
                    open={openModal === 'assignManifest'}
                    onClose={() => setOpenModal(null)}
                    onSuccess={() => refetchManifests()}
                />
            )}
            <ArchiveManifestModal
                isArchiving={archiving}
                open={openModal === 'archiveManifest'}
                onConfirm={handleConfirmArchive}
                onClose={() => setOpenModal(null)}
            />
            <UnarchiveManifestModal
                isUnarchiving={unarchiving}
                open={openModal === 'unarchiveManifest'}
                onConfirm={handleConfirmUnarchive}
                onClose={() => setOpenModal(null)}
            />
        </StyledPageWrapper>
    );
};

export default FreightForwarderAdminDashboard;
